/**
 * 69.3b, the ship as a body with parts — step 2 (BUILDLIST 69.3b's note; the owner, Oct 20: "most of those outcomes should
 * just lead to a likely crash, but we could use those behaviors for now. 1. for early destructibility we can just have a
 * new wave ship show up afterwards. Not even .50 should hurt, rockets and shells for now. 3. downed riders maybe have some
 * chance of survival"): with `GameConfig.shipsDestructible`, a ship is a body — its hull takes blasts (rockets, grenades,
 * the 40 mm, shells: the damage table's `hull` row, which a bullet and the .50 wear not at all), its marked parts (the
 * nacelles, the cockpit: core/shipBody.ts SHIP_PARTS) by their own distance from the burst, each out of its own health.
 * What a loss does, as placeholders for the crash to come (211 (2)): **a nacelle gone** lowers the ship's cruise by its
 * share of the lift (`cruiseRadius`: the look leans); **two nacelles, the cockpit, or the hull at 0 loses the ship** — the
 * flight ends where it is (a likely crash), its riders still aboard each survive by DROPSHIP.riderSurvival (a keyed roll:
 * the same answer in every engine) and stand on the ground under it, the rest stay down (their tickets unspent — they
 * wait for the next ship); its container falls where it was (a structure, breakable cover), a vehicle on the cables a
 * wreck; **a wave ship lost** (parked on its pad, or on a run) brings a new one to the pad DROPSHIP.replaceSeconds later
 * (`shipPads[team].backAt`; the side is never without its ship for long). Off: no field is written, no round or blast
 * reaches a ship, every byte as before. Pure; the Match drains `dropships.lost` into events each tick.
 */
import type { GameState, DropshipFlight, ShipCrash, StructureState, Vec3, LighterRun } from '../types';
import type { BlowCause } from '../types/body';
import { DAMAGE_VS } from '../constants/damage';
import { DROPSHIP } from '../constants/action';
import { DROPSHIP_ANCHORS } from '../constants/dropship';
import { SHIP_PARTS, SHIP_BODY_HEALTH, shipShapes, shipBodyOf, type ShipShape } from './shipBody';
import { containerAt, vehicleAt } from './dropship';
import { startCrash, startCrashAt, crashBlow } from './shipCrash';
import { flightNow, lighterNow } from './shipNow';
import { LIGHTER } from '../constants/action';
import { LIGHTER_PARTS, LIGHTER_FANS, LIGHTER_ID_BASE, isLighterId, lighterWholeParts } from './lighterBody';
import { lighterById } from './lighter';
import { executeRespawn } from './respawn';
import { loseParked, parkedOfId } from './helipad';   // 69.74 step 2c
import { SeededRandom } from './random';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { PHYS_DT } from '../constants/physics';
import { vecAdd, vecScale, vecSub, vecDot, vecLength } from '../math';
import { mayMeet, partEntry, type Prim } from './hits';

/** A helicopter's body (69.74 step 2c: a parked one's, as a flight's). */
const HELI = { craft: 'heli' as const };

/** Whether ships are bodies here (the switch). */
export function shipsOn(state: Pick<GameState, 'config'>): boolean {
  return !!state.config.shipsDestructible;
}
/** The match's clock (the action tick; the turn number on the turn clock). */
const tickOf = (state: Pick<GameState, 'tick' | 'turnNumber'>): number => state.tick ?? state.turnNumber;

/** A ship's body by its shape reference: a flight (id ≥ 0) or the pad ship of team −1 − id. */
function shipOf(state: GameState, id: number): { flight?: DropshipFlight; crash?: ShipCrash; lighter?: LighterRun; parked?: { team: number; spot: number }; team: number; health: number; parts: number[] } | null {
  if (isLighterId(id)) {   // 69.10 step 4: a lighter's run (its crash, once lost, is in the crashes under the same id)
    const r = lighterById(state, id - LIGHTER_ID_BASE);
    if (r) return { lighter: r, team: r.team, health: r.health ?? LIGHTER.health, parts: r.parts ?? lighterWholeParts() };
    const c = (state.dropships?.crashes ?? []).find(x => x.id === id);
    return c ? { crash: c, team: c.team, health: c.health, parts: [] } : null;
  }
  if (id >= 0) {
    const f = (state.dropships?.flights ?? []).find(x => x.id === id);
    if (f) { const b = shipBodyOf(f); return { flight: f, team: f.team, health: f.health ?? b.health, parts: f.parts ?? b.table.map(m => m.share * b.health) }; }   // (69.59: a helicopter's own)
    const c = (state.dropships?.crashes ?? []).find(x => x.id === id);   // 69.3b, the ship's crash: a falling ship takes blows (its hull only)
    return c ? { crash: c, team: c.team, health: c.health, parts: [] } : null;
  }
  const parked = parkedOfId(id);
  if (parked) {   // 69.74 step 2c: a helicopter parked on its pad (a helicopter's body: soft, its own parts)
    const x = state.dropships?.pads?.[parked.team]?.spots[parked.spot];
    if (!x || (x.state !== 'ready' && x.state !== 'turn')) return null;
    const b = shipBodyOf(HELI);
    return { parked, team: parked.team, health: x.health ?? b.health, parts: x.parts ?? b.table.map(m => m.share * b.health) };
  }
  const team = -1 - id, pad = state.shipPads?.[team];
  return pad ? { team, health: pad.health ?? SHIP_BODY_HEALTH, parts: pad.parts ?? SHIP_PARTS.map(m => m.share * SHIP_BODY_HEALTH) } : null;
}

/** The nearest distance from `pt` to a box prim (world units). */
function primDistance(pt: Vec3, p: Prim): number {
  if (p.cyl) return Infinity;
  const rel = vecSub(pt, p.c);
  const dx = Math.max(0, Math.abs(vecDot(rel, p.ax)) - p.hx), dy = Math.max(0, Math.abs(vecDot(rel, p.ay)) - p.hy), dz = Math.max(0, Math.abs(vecDot(rel, p.az)) - p.hz);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** The ship's shape at the tick, by its id (null when it is not there). */
function shapeOf(state: GameState, id: number, planetIndex: number, tick: number): ShipShape | null {
  return shipShapes(state, planetIndex, tick).find(s => s.ref.id === id) ?? null;
}

/**
 * A blow on ship `id` from `point`: `amount` through the table for the hull; the marked part nearest the point, within
 * `partReach` (world), takes it too out of its own health (a direct hit on a nacelle); with `partAmounts` (a blast's, by
 * each marked part's own distance) those instead. The state with the ship worn, lost if it is, or the same state.
 */
export function shipBlow(state: GameState, id: number, planetIndex: number, point: Vec3, amount: number, cause: BlowCause, partAmounts?: number[] | null, partReach = 0): GameState {
  if (!shipsOn(state)) return state;
  const ship = shipOf(state, id);
  if (!ship) return state;
  const heli = ship.flight?.craft === 'heli' || !!ship.parked;   // (69.59 step 1: a helicopter's hull is soft — rounds wear it; 69.74 step 2c: parked too)
  const blow = amount * DAMAGE_VS[cause][heli ? 'soft' : 'hull'];
  if (!(blow > 0)) return state;
  const tick = tickOf(state);
  if (ship.crash) return crashBlow(state, id, blow, tick);   // (a falling ship: worn toward its break-up in the air)
  if (ship.health <= 0) return state;
  const parts = ship.parts.slice();
  // (69.10 step 4: a lighter's table and lifters — its fans — in place of the dropship's nacelles)
  const b = shipBodyOf(ship.parked ? HELI : ship.flight), table = ship.lighter ? LIGHTER_PARTS : b.table, lifters = ship.lighter ? LIGHTER_FANS : b.lifters;
  if (partAmounts) {
    for (let k = 0; k < parts.length; k++) parts[k] = Math.max(0, parts[k] - (partAmounts[k] ?? 0) * DAMAGE_VS[cause][heli ? 'soft' : 'hull']);
  } else if (partReach > 0) {
    const sh = shapeOf(state, id, planetIndex, tick);
    if (sh) {
      let best = -1, bestD = partReach;
      table.forEach((m, k) => { if (parts[k] <= 0) return; const d = primDistance(point, sh.prims[m.part]); if (d < bestD) { bestD = d; best = k; } });
      if (best >= 0) parts[best] = Math.max(0, parts[best] - blow);
    }
  }
  const health = Math.max(0, ship.health - blow);
  const nacellesLost = table.filter((m, k) => lifters.includes(m.part) && parts[k] <= 0).length;
  const cockpitLost = !ship.lighter && table.some((m, k) => m.part === b.cockpit && parts[k] <= 0);
  const tailLost = b.tail !== undefined && table.some((m, k) => m.part === b.tail && parts[k] <= 0);   // (69.59: a helicopter's tail rotor gone, it spins in)
  const lost = health <= 0 || cockpitLost || tailLost || nacellesLost >= 2;
  const lostBefore = table.filter((m, k) => lifters.includes(m.part) && ship.parts[k] <= 0).length;
  // lost: the crash starts from the pose as it was (the health and parts written, no cruise drop: the fall is the crash's) — by the hull at 0 first (a blast that broke it: it comes down intact), else the nacelles, else the cockpit
  if (lost) return loseShip(writeShip(state, id, health, parts, 0), id, planetIndex, tick, health <= 0 ? 'hull' : nacellesLost >= 2 || tailLost ? 'nacelle' : 'cockpit');
  return writeShip(state, id, health, parts, nacellesLost - lostBefore);
}

/** The ship's health and parts written (a flight's, or the pad ship's); a nacelle lost lowers a flight's cruise by its share of the lift. */
function writeShip(state: GameState, id: number, health: number, parts: number[], lostNacelles: number): GameState {
  if (isLighterId(id)) {   // 69.10 step 4: the run's
    return { ...state, lighters: { ...state.lighters!, runs: state.lighters!.runs.map(r => (r.id === id - LIGHTER_ID_BASE ? { ...r, health, parts } : r)) } };
  }
  if (id >= 0) {
    return { ...state, dropships: { ...state.dropships!, flights: state.dropships!.flights.map(f => {
      if (f.id !== id) return f;
      const out: DropshipFlight = { ...f, health, parts };
      if (lostNacelles > 0 && f.cruiseRadius !== undefined) {
        // the lift's share: each nacelle a quarter — the cruise comes down by it (over the ground under it the pose eases)
        const planet = state.planets[f.planetIndex];
        const ground = planet.radius, over = f.cruiseRadius - ground;
        out.cruiseRadius = ground + over * (1 - 0.25 * lostNacelles);
      }
      return out;
    }) } };
  }
  const parked = parkedOfId(id);
  if (parked) {   // 69.74 step 2c: the parked helicopter's, on its spot
    const a = state.dropships!, pad = a.pads![parked.team];
    return { ...state, dropships: { ...a, pads: { ...a.pads, [parked.team]: { ...pad, spots: pad.spots.map((x, k) => (k === parked.spot ? { ...x, health, parts } : x)) } } } };
  }
  const team = -1 - id, pad = state.shipPads![team];
  return { ...state, shipPads: { ...state.shipPads, [team]: { ...pad, health, parts } } };
}

/**
 * A ship lost: the flight ends where it is (removed), its riders aboard each survive by the dial and stand on the ground
 * under it (respawned there, their tickets as a wave's), the rest stay down; its container falls where it was (a
 * structure), a vehicle on the cables a wreck; a wave ship (a parked one, or a run from the pad) brings a new one to the
 * pad DROPSHIP.replaceSeconds later. The loss is filed in `dropships.lost` for the Match's events.
 */
export function loseShip(state: GameState, id: number, planetIndex: number, tick: number, cause: ShipCrash['cause'] = 'hull'): GameState {
  const rng = new SeededRandom(`${state.config.seed ?? ''}-ship-lost-${id}-${tick}`);
  const planet = state.planets[planetIndex];
  let next = state;
  const survived: number[] = [], lost: number[] = [];
  let at: Vec3 | null = null, team = -1;
  if (isLighterId(id)) {
    // 69.10 step 4: a lighter lost falls from its pose with its frames (the loot at the impact); the run is gone, the side's cadence restarts from now
    const r = lighterById(state, id - LIGHTER_ID_BASE);
    const now = r ? lighterNow(state, r, tick) : null;   // 69.17 step 1: the ship as it is now
    if (!r || !now) return state;
    const pose = now.lighter!.pose, fr = now.frame, vel = now.vel;
    const crash = startCrashAt(state, { id, team: r.team, planetIndex: r.planetIndex, pos: pose.pos, vel, frame: fr, health: r.health ?? LIGHTER.health, payload: 'frames', frames: pose.aboard, loadKinds: r.load.slice(0, pose.aboard), carrying: pose.aboard > 0, riders: [], facing: fr.front, lighter: true }, cause, tick);
    const l = state.lighters!;
    next = { ...next, lighters: { ...l, runs: l.runs.filter(x => x.id !== r.id), lastTouch: { ...(l.lastTouch ?? {}), [r.team]: tick } },
      dropships: { ...(state.dropships ?? { left: {}, issued: {}, flights: [], next: 0 }), crashes: [...(state.dropships?.crashes ?? []), crash], lost: [...(state.dropships?.lost ?? []), { team: r.team, id, planetIndex, at: pose.dir, tick, survived: [], lost: [] }] } };
    return next;
  }
  const parked = parkedOfId(id);
  if (parked) return loseParked(state, parked.team, parked.spot, tick);   // 69.74 step 2c: a parked helicopter, its wreck on its spot
  if (id >= 0) {
    const f = (state.dropships?.flights ?? []).find(x => x.id === id);
    if (!f) return state;
    team = f.team;
    const pose = flightNow(state, f, tick)?.flight!.pose;   // 69.17 step 1: the ship as it is now
    at = pose ? pose.dir : f.land;
    // 69.3b, the ship's crash (DECISIONS 223): the flight falls from here (core/shipCrash.ts) — its riders aboard and its load ride it down; the roll is the impact's
    // (69.59: a helicopter's, so — down whole: one piece, no break-up in the air)
    const crash0 = startCrash(state, f, cause, tick), crash = crash0 && f.craft ? (({ brokeUp: _b, ...rest }) => ({ ...rest, craft: f.craft, pieces: 1 as const }))(crash0) : crash0;
    if (crash) {
      const flights = state.dropships!.flights.filter(x => x.id !== id);
      next = { ...next, dropships: { ...state.dropships!, flights, crashes: [...(state.dropships!.crashes ?? []), crash], lost: [...(state.dropships!.lost ?? []), { team, id, planetIndex, at, tick, survived: [], lost: [] }] } };
      if (f.fromPad && next.shipPads?.[f.padOf ?? team]) next = { ...next, shipPads: { ...next.shipPads, [f.padOf ?? team]: newShipAt(next.shipPads[f.padOf ?? team], tick) } };   // (69.17 step 3: a taken ship lost, its own pad waits for a new one)
      return next;
    }
    const aboard = [...f.riders.slice(f.out), ...(f.piloted ? [f.piloted.pilot] : [])].filter(rid => { const p = state.players.find(q => q.id === rid); return !!p && (!p.alive || !!p.boarded); });
    for (const rid of aboard) (rng.next() < DROPSHIP.riderSurvival ? survived : lost).push(rid);
    // the flight gone; its load where it was
    const flights = state.dropships!.flights.filter(x => x.id !== id);
    let structures = next.structures;
    const ground = { ...f, land: at, facing: f.facing } as DropshipFlight;
    if (pose?.carrying && f.payload === 'troops' && f.containerId === undefined && f.craft !== 'heli') {   // (69.59: a helicopter's riders are in its cabin: no container)
      const cid = structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
      structures = [...structures, { ...containerAt(state, ground, cid), ownerId: f.riders[0] ?? -1 }];
    } else if (pose?.carrying && f.payload === 'vehicle' && f.vehicle && f.vehicleId === undefined) {
      const vid = structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
      const v: StructureState = { ...vehicleAt(state, ground, vid, f.riders[0] ?? state.players.find(p => p.teamIndex === f.team)?.id ?? -1), health: 0 };
      structures = [...structures, v];
    }
    next = { ...next, structures, dropships: { ...state.dropships!, flights, lost: [...(state.dropships!.lost ?? []), { team, id, planetIndex, at, tick, survived, lost }] } };
    if (f.fromPad && next.shipPads?.[f.padOf ?? team]) next = { ...next, shipPads: { ...next.shipPads, [f.padOf ?? team]: newShipAt(next.shipPads[f.padOf ?? team], tick) } };
    // the riders aboard alive (208: boarded at the pad) who did not make it are down, their tickets kept for the next ship
    next = { ...next, players: next.players.map(p => (lost.includes(p.id) && p.alive ? { ...p, alive: false, health: 0, boarded: undefined } : p)) };
  } else {
    team = -1 - id;
    const pad = state.shipPads?.[team];
    if (!pad) return state;
    at = pad.dir;
    for (const p of state.players) if (p.boarded && p.teamIndex === team && p.alive) (rng.next() < DROPSHIP.riderSurvival ? survived : lost).push(p.id);
    next = { ...next, shipPads: { ...state.shipPads, [team]: newShipAt(pad, tick) },
      dropships: { ...(state.dropships ?? { left: {}, issued: {}, flights: [], next: 0 }), lost: [...(state.dropships?.lost ?? []), { team, id, planetIndex, at, tick, survived, lost }] } };
    // the boarded who did not make it are down; the survivors stand by the pad
    next = { ...next, players: next.players.map(p => lost.includes(p.id) ? { ...p, alive: false, health: 0, boarded: undefined } : survived.includes(p.id) ? { ...p, boarded: undefined } : p) };
  }
  // the survivors of a flight: on the ground under the ship, in a loose ring (the exits' spots) — a rider alive aboard out of its
  // seat, a fallen one back as a wave's arrival (no ticket: it stays down)
  if (id >= 0 && at) {
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    let i = 0;
    for (const rid of survived) {
      const before = next, p = next.players.find(q => q.id === rid);
      if (!p) continue;
      if (!p.alive) next = executeRespawn(next, rid);
      if (!p.alive && next === before) continue;
      const e = DROPSHIP_ANCHORS.exits[i % DROPSHIP_ANCHORS.exits.length];
      const spot = shipSpot(at, planet.radius, e[0], e[2], person);
      next = { ...next, players: next.players.map(p => (p.id === rid ? { ...p, planetIndex, surfaceDir: spot, boarded: undefined, rampWalk: undefined, stance: undefined } : p)) };
      i++;
    }
  }
  return next;
}

/** The pad with its ship gone and a new one due at `tick` + DROPSHIP.replaceSeconds, whole. */
function newShipAt(pad: NonNullable<GameState['shipPads']>[number], tick: number): NonNullable<GameState['shipPads']>[number] {
  const { health: _h, parts: _p, ...rest } = pad;
  return { ...rest, backAt: tick + Math.round(DROPSHIP.replaceSeconds / PHYS_DT) };
}

/** A point `x` across, `z` along from `dir` on the ground (people × `person`, world), by a small tangent step in an upright frame there. */
function shipSpot(dir: Vec3, radius: number, x: number, z: number, person: number): Vec3 {
  const a = Math.abs(dir.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1 = { x: dir.y * a.z - dir.z * a.y, y: dir.z * a.x - dir.x * a.z, z: dir.x * a.y - dir.y * a.x }, l = vecLength(e1) || 1;
  const t1 = vecScale(e1, 1 / l), t2 = { x: dir.y * t1.z - dir.z * t1.y, y: dir.z * t1.x - dir.x * t1.z, z: dir.x * t1.y - dir.y * t1.x };
  const d = vecAdd(dir, vecScale(vecAdd(vecScale(t1, x * person), vecScale(t2, z * person)), 1 / radius));
  const dl = vecLength(d);
  return { x: d.x / dl, y: d.y / dl, z: d.z / dl };
}

/**
 * A blast's pass over the ships on planet `planetIndex` (the two blast doors call it: core/rocket.ts rocketBlast for the
 * bursts, core/resolveImpact.ts for a shell): each ship within the blast's reach (`radius` × 2.2, a structure's) by its
 * nearest prim takes `damage × (1 − d / reach)` through the hull's row, its marked parts each by their own distance.
 */
export function shipBlastPass(state: GameState, planetIndex: number, point: Vec3, radius: number, damage: number, cause: BlowCause): GameState {
  if (!shipsOn(state) || !(damage > 0) || !(radius > 0)) return state;
  const tick = tickOf(state), reach = radius * 2.2;
  let next = state;
  for (const sh of shipShapes(state, planetIndex, tick)) {
    let d = Infinity;
    for (const p of sh.prims) d = Math.min(d, primDistance(point, p));
    if (d >= reach) continue;
    const amount = damage * Math.max(0, 1 - d / reach);
    const f = sh.ref.id >= 0 && !isLighterId(sh.ref.id) ? (state.dropships?.flights ?? []).find(x => x.id === sh.ref.id) ?? (state.dropships?.crashes ?? []).find(x => x.id === sh.ref.id) : undefined;   // (a falling helicopter's its own body too)
    const partAmounts = shipBodyOf(parkedOfId(sh.ref.id) ? HELI : f).table.map(m => { const pd = primDistance(point, sh.prims[m.part]); return pd < reach ? damage * Math.max(0, 1 - pd / reach) : 0; });   // (69.59: a helicopter's own)
    next = shipBlow(next, sh.ref.id, planetIndex, point, amount, cause, partAmounts);
  }
  return next;
}

/** A ship's health share (0 … 1) for the look (the lean, the smoke): the hull's, and each marked part's. */
export function shipWear(state: GameState, id: number): { hull: number; parts: number[] } | null {
  const ship = shipOf(state, id);
  if (!ship) return null;
  const b = shipBodyOf(ship.flight);
  return { hull: ship.health / b.health, parts: b.table.map((m, k) => ship.parts[k] / (m.share * b.health)) };
}

/** 69.3b: where a→b first enters a ship on planet `planetIndex` (0 … 1) and which (null: none; nothing with the switch off); never ship `skip`. */
export function firstShipHit(a: Vec3, b: Vec3, planetIndex: number, state: GameState, skip?: number): { t: number; id: number } | null {
  if (!shipsOn(state)) return null;
  let best: { t: number; id: number } | null = null;
  for (const sh of shipShapes(state, planetIndex, tickOf(state))) {
    if (sh.ref.id === skip) continue;   // (69.59 step 3: a round's own ship, `skip`: a helicopter's guns fire past its nose and skids)
    const meet = mayMeet(a, b, sh as unknown as Parameters<typeof mayMeet>[2]);
    if (meet === 0) continue;
    for (const p of sh.prims) {
      const t = partEntry(a, b, p, meet);
      if (t !== null && (!best || t < best.t)) best = { t, id: sh.ref.id };
    }
  }
  return best;
}

/**
 * 65.49 step 2: how near the stretch a→b passes ship `id`'s body on planet `planetIndex` (world units; 0 when it enters a
 * part, Infinity when the ship is not there) — else the nearest of its parts to the stretch's ends and middle. A stretch is
 * a tick of a missile's flight (a person at most), so the sampled nearest is within half of one of the true nearest.
 */
export function shipPass(a: Vec3, b: Vec3, planetIndex: number, state: GameState, id: number): number {
  const sh = shapeOf(state, id, planetIndex, tickOf(state));
  if (!sh) return Infinity;
  const meet = mayMeet(a, b, sh as unknown as Parameters<typeof mayMeet>[2]);
  if (meet !== 0) for (const p of sh.prims) if (partEntry(a, b, p, meet) !== null) return 0;
  const m = vecScale(vecAdd(a, b), 0.5);
  let d = Infinity;
  for (const p of sh.prims) d = Math.min(d, primDistance(a, p), primDistance(m, p), primDistance(b, p));
  return d;
}

/**
 * 65.49 (the owner: two hits that "didn't go down"): where a stretch a→b that passed within a fuse of ship `id` met it —
 * the point where it entered a part, else the point of the ship's parts nearest the stretch (its ends and middle, each
 * clamped into each box): the burst's place, on the hull, not the missile's own (up to its fuse off it). Null when gone.
 */
export function shipContact(a: Vec3, b: Vec3, planetIndex: number, state: GameState, id: number): Vec3 | null {
  const sh = shapeOf(state, id, planetIndex, tickOf(state));
  if (!sh) return null;
  const meet = mayMeet(a, b, sh as unknown as Parameters<typeof mayMeet>[2]);
  let first: number | null = null;
  if (meet !== 0) for (const p of sh.prims) { const t = partEntry(a, b, p, meet); if (t !== null && (first === null || t < first)) first = t; }
  if (first !== null) return vecAdd(a, vecScale(vecSub(b, a), first));
  const m = vecScale(vecAdd(a, b), 0.5);
  let best: Vec3 | null = null, bestD = Infinity;
  for (const p of sh.prims) {
    if (p.cyl) continue;
    for (const q of [a, m, b]) {
      const rel = vecSub(q, p.c), x = Math.max(-p.hx, Math.min(p.hx, vecDot(rel, p.ax))), y = Math.max(-p.hy, Math.min(p.hy, vecDot(rel, p.ay))), z = Math.max(-p.hz, Math.min(p.hz, vecDot(rel, p.az)));
      const on = vecAdd(p.c, vecAdd(vecScale(p.ax, x), vecAdd(vecScale(p.ay, y), vecScale(p.az, z)))), d = vecLength(vecSub(q, on));
      if (d < bestD) { bestD = d; best = on; }
    }
  }
  return best;
}

/** 69.3b: whether a ship on planet `planetIndex` stands across a→b (a line of sight, a cover test); nothing with the switch off. */
export function shipBlocks(a: Vec3, b: Vec3, planetIndex: number, state: GameState): boolean {
  return firstShipHit(a, b, planetIndex, state) !== null;
}

